import { Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger } from '@@/ui/primitives';
import { FilterIcon, SearchIcon, TrashIcon } from 'lucide-react';
import { SearchConnectResourceInput } from './dynamic-search';
import { FilterInitiationMethod } from './filter-initiation-method';
import { clsx } from 'clsx';
import { FilterChannel } from './filter-channel';
import { FilterTimeline } from './filter-timeline';
import { FilterAttributes, AttributesFilter } from './filter-attributes';
import { SearchContactsTimeRangeType } from '@aws-sdk/client-connect';

export function getDefaultTimeline() {
  return {
    startTime: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] + 'T00:00:00Z',
    endTime: new Date().toISOString().split('T')[0] + 'T23:59:59Z',
    type: 'INITIATION_TIMESTAMP' as SearchContactsTimeRangeType
  };
}

export const ContactSearch = (props: {
  contactId: string;
  onContactIdChange: (value: string) => void;
  selectedAgentIds: string[];
  onAgentIdsChange: (ids: string[]) => void;
  selectedQueueIds: string[];
  onQueueIdsChange: (ids: string[]) => void;
  selectedInitiationMethods: string[];
  onInitiationMethodsChange: (methods: string[]) => void;
  selectedChannels: string[];
  onChannelsChange: (channels: string[]) => void;
  includePending: boolean;
  onIncludePendingChange: (value: boolean) => void;
  timeline: {
    startTime: string;
    endTime: string;
    type: SearchContactsTimeRangeType;
  };
  onTimelineChange: (timeline: { startTime: string; endTime: string; type: SearchContactsTimeRangeType }) => void;
  segmentAttributes: AttributesFilter;
  onSegmentAttributesChange: (value: AttributesFilter) => void;
  contactAttributes: AttributesFilter;
  onContactAttributesChange: (value: AttributesFilter) => void;
}) => {
  const hasContactId = props.contactId.trim().length > 0;

  const hasFilters =
    hasContactId ||
    props.selectedAgentIds.length > 0 ||
    props.selectedQueueIds.length > 0 ||
    JSON.stringify(props.timeline) !== JSON.stringify(getDefaultTimeline()) ||
    props.selectedInitiationMethods.length > 0 ||
    props.segmentAttributes.criteria.length > 0 ||
    props.contactAttributes.criteria.length > 0;

  function reset() {
    props.onContactIdChange('');
    props.onAgentIdsChange([]);
    props.onQueueIdsChange([]);
    props.onInitiationMethodsChange([]);
    props.onChannelsChange([]);
    props.onTimelineChange(getDefaultTimeline());
    props.onSegmentAttributesChange({
      criteria: [],
      matchType: 'MATCH_ALL'
    });
    props.onContactAttributesChange({
      criteria: [],
      matchType: 'MATCH_ALL'
    });
  }

  return (
    <div className='flex flex-col gap-3 py-2 px-3.5'>
      <div className='flex flex-col gap-1'>
        <div className='flex gap-1 items-center relative'>
          <SearchIcon className='size-4 text-neutral-400 absolute left-2.5 top-1/2 transform -translate-y-1/2' />
          <Input
            placeholder='Contact ID'
            value={props.contactId}
            onChange={(e) => props.onContactIdChange(e.target.value)}
            className='text-sm! pl-8 shadow-none'
            pattern='/^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})$/'
          />
        </div>
      </div>
      <div className='flex items-center justify-between pb-1'>
        <div className='flex-grow flex items-center text-sidebar-foreground/70 text-xs gap-2 pl-0.5'>
          <Checkbox
            title='Include Live Contacts'
            checked={props.includePending}
            onCheckedChange={(checked) => props.onIncludePendingChange(!!checked)}
          />
          <span className='text-xs font-medium text-sidebar-foreground text-nowrap'>Live Contacts</span>
        </div>

        {hasFilters && <span className='size-1.5 mr-2 rounded-full bg-sidebar-foreground' />}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              size='xs'
              variant='outline'
              className={clsx(
                'text-xs! shadow-none font-medium!'
                // !advancedOpen && 'border-transparent',
                // advancedOpen && ''
              )}
              // onClick={() => setAdvancedOpen(!advancedOpen)}
              disabled={hasContactId}
            >
              {/* {advancedOpen ? <FilterXIcon className='size-3' /> : <FilterIcon className='size-3' />} */}
              <FilterIcon className='size-3' />
              Advanced
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-72 font-sans p-3 py-4' align='start'>
            <div className='grid gap-4'>
              <div className='space-y-2'>
                <h4 className='leading-none font-medium text-sm'>Advanced Filters</h4>
                <p className='text-muted-foreground text-xs'>Refine your contact search with additional filters.</p>
              </div>
              <div className='flex flex-col gap-3'>
                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Timeline</p>
                  <FilterTimeline disabled={hasContactId} selected={props.timeline} onSelect={props.onTimelineChange} />
                </div>

                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Agents</p>
                  <SearchConnectResourceInput
                    type='USER'
                    disabled={hasContactId}
                    selected={props.selectedAgentIds}
                    onSelect={props.onAgentIdsChange}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Queues</p>
                  <SearchConnectResourceInput
                    type='QUEUE'
                    disabled={hasContactId}
                    selected={props.selectedQueueIds}
                    onSelect={props.onQueueIdsChange}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Initiation Methods</p>
                  <FilterInitiationMethod
                    disabled={hasContactId}
                    selected={props.selectedInitiationMethods}
                    onSelect={props.onInitiationMethodsChange}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Channels</p>
                  <FilterChannel
                    disabled={hasContactId}
                    selected={props.selectedChannels}
                    onSelect={props.onChannelsChange}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Segment Attributes</p>
                  <FilterAttributes
                    disabled={hasContactId}
                    selected={props.segmentAttributes}
                    onSelect={props.onSegmentAttributesChange}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <p className='text-xs text-sidebar-foreground/70 font-medium pl-0.5'>Contact Attributes</p>
                  <FilterAttributes
                    disabled={hasContactId}
                    selected={props.contactAttributes}
                    onSelect={props.onContactAttributesChange}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <Button variant='outline' size={'sm'} className='text-sm!' disabled={!hasFilters} onClick={reset}>
                    <TrashIcon className='size-4' />
                    Clear all filters
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
